import { useState } from "react";
import { Archive, ArrowLeft, Pencil, Star } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRuntime } from "../../react/runtime/RuntimeContext";
import type {
    AssistantSessionListItem,
    AssistantSessionListResponse,
} from "./types";

// In-panel conversation history (Enh 05) — replaces the cramped header
// popover. Renders as a full-width view INSIDE the assistant panel: a
// "← Conversations" header + the past sessions (newest first) with
// resume / star / rename / archive. Solid panel surface (no transparency).

interface Props {
    /** Resume a session by id — AssistantPanel seeds its session state. */
    onResume: (sessionId: string) => void;
    /** Return to the chat view. */
    onBack: () => void;
}

export function AssistantHistoryList({ onResume, onBack }: Props) {
    const runtime = useRuntime();
    const qc = useQueryClient();

    const sessionsQ = useQuery({
        queryKey: ["assistant.sessions"],
        queryFn: async () => {
            const env = (await runtime.services.assistant.listSessions({ limit: 50 })) as unknown as AssistantSessionListResponse;
            return env?.items ?? [];
        },
        staleTime: 30 * 1000,
    });

    const sessions = (sessionsQ.data ?? []) as AssistantSessionListItem[];

    const handleStar = async (sessionId: string) => {
        await runtime.services.assistant.starSession(sessionId);
        qc.invalidateQueries({ queryKey: ["assistant.sessions"] });
    };

    const handleRename = async (sessionId: string, currentTitle: string | null) => {
        const next = window.prompt("Rename conversation", currentTitle ?? "");
        if (next == null || next.trim() === "") return;
        await runtime.services.assistant.renameSession(sessionId, next.trim());
        qc.invalidateQueries({ queryKey: ["assistant.sessions"] });
    };

    const handleArchive = async (sessionId: string) => {
        await runtime.services.assistant.archiveSession(sessionId);
        qc.invalidateQueries({ queryKey: ["assistant.sessions"] });
    };

    return (
        <div className="assistant-history" data-testid="assistant-history">
            <div className="assistant-history__head">
                <button
                    type="button"
                    className="assistant-history__back"
                    onClick={onBack}
                    data-testid="assistant-history-back"
                >
                    <ArrowLeft size={14} />
                    <span>Conversations</span>
                </button>
            </div>
            <div className="assistant-history__body">
                {sessionsQ.isLoading ? (
                    <div className="assistant-history__status">Loading…</div>
                ) : sessions.length === 0 ? (
                    <div className="assistant-history__status">No previous conversations.</div>
                ) : (
                    <ul className="assistant-history__list">
                        {sessions.map((s) => (
                            <HistoryRow
                                key={s.id}
                                session={s}
                                onResume={onResume}
                                onStar={handleStar}
                                onRename={handleRename}
                                onArchive={handleArchive}
                            />
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function HistoryRow({
    session,
    onResume,
    onStar,
    onRename,
    onArchive,
}: {
    session: AssistantSessionListItem;
    onResume: (sessionId: string) => void;
    onStar: (sessionId: string) => void;
    onRename: (sessionId: string, currentTitle: string | null) => void;
    onArchive: (sessionId: string) => void;
}) {
    const [busy, setBusy] = useState(false);
    const guard = async (fn: () => Promise<void> | void) => {
        if (busy) return;
        setBusy(true);
        try {
            await fn();
        } finally {
            setBusy(false);
        }
    };

    const title = session.title ?? "Untitled conversation";

    return (
        <li className="assistant-history-popover__row" data-testid={`history-row-${session.id}`}>
            <button
                type="button"
                className="assistant-history-popover__row-resume"
                onClick={() => onResume(session.id)}
                title="Resume this conversation"
            >
                <span className="assistant-history-popover__row-title">{title}</span>
                <span className="assistant-history-popover__row-meta">
                    {session.mode === "contextual" ? "contextual" : "global"}
                    {session.anchored_action_key ? ` · ${session.anchored_action_key}` : ""}
                </span>
            </button>
            <div className="assistant-history-popover__row-actions">
                <button
                    type="button"
                    className={`assistant-history-popover__row-action${session.is_starred ? " assistant-history-popover__row-action--active" : ""}`}
                    onClick={() => guard(() => onStar(session.id))}
                    disabled={busy}
                    title={session.is_starred ? "Unstar" : "Star"}
                    data-testid={`history-star-${session.id}`}
                >
                    <Star size={12} />
                </button>
                <button
                    type="button"
                    className="assistant-history-popover__row-action"
                    onClick={() => guard(() => onRename(session.id, session.title))}
                    disabled={busy}
                    title="Rename"
                    data-testid={`history-rename-${session.id}`}
                >
                    <Pencil size={12} />
                </button>
                <button
                    type="button"
                    className="assistant-history-popover__row-action"
                    onClick={() => guard(() => onArchive(session.id))}
                    disabled={busy}
                    title="Archive"
                    data-testid={`history-archive-${session.id}`}
                >
                    <Archive size={12} />
                </button>
            </div>
        </li>
    );
}
